import type { ApiClient } from '../client';
import {
  AmazonDoorAccessCountSchema,
  AmazonDoorAccessLocationSchema,
  type AmazonDoorAccessLocation,
} from '../schemas/amazonDoorAccess';

// How many locations sit on one side of the Amazon door-access gate.
//
// Returns the COUNT, not the list: the whole filtered table travels, and the screen reads a single
// integer off it, so the rows never reach the query cache.
//
// `enabled` is omitted-means-everything on the wire, so it is always sent explicitly — the
// configured total is the sum of the two filtered reads, never the unfiltered one. It is a boolean
// rendered by the caller's own `String()`, so nothing user-supplied reaches the query string.
export async function fetchDoorAccessCount(client: ApiClient, enabled: boolean): Promise<number> {
  const data = await client.get<unknown>(
    `/app_service/panel/v1/amazon/door-access/locations?enabled=${String(enabled)}`,
  );
  return AmazonDoorAccessCountSchema.parse(data).locations.length;
}

// The state of one location at the gate. The id is the ONLY human-sourced value the panel puts in
// a PATH, and it arrives already filtered to an integer ≥ 1 by its caller: `String()` over a number
// can only write digits, so there is nothing here to escape and nothing to inject.
//
// **It never 404s.** An id nobody has configured answers `200` with the default state (denied) and
// `updated_at: null`, so there is no «not found» branch to write — and no read on this screen can
// tell a real location from an id that is no location at all.
export async function fetchDoorAccessLocation(
  client: ApiClient,
  locationId: number,
): Promise<AmazonDoorAccessLocation> {
  const data = await client.get<unknown>(
    `/app_service/panel/v1/amazon/door-access/locations/${String(locationId)}`,
  );
  return AmazonDoorAccessLocationSchema.parse(data);
}

// Move a batch of locations to one side of the Amazon door-access gate. An idempotent bulk upsert:
// one request, never chunked, because one request is one outcome and that is what makes the
// counter delta interpretable.
//
// `enabled: false` rather than a DELETE: it keeps the row and its `updated_at`, so «when did this
// last change» survives — and there is no bulk DELETE to use anyway.
//
// The ids travel in the BODY. A query string carrying a thousand of them would be recorded whole by
// every proxy and access log between here and app-service, and would outlive the request there.
//
// Resolves `void`: the endpoint answers 200 with an empty body, so there is no receipt to parse and
// nothing for Zod to validate on this path.
export async function setDoorAccessForLocations(
  client: ApiClient,
  locationIds: number[],
  enabled: boolean,
): Promise<void> {
  await client.post('/app_service/panel/v1/amazon/door-access/locations', {
    location_ids: locationIds,
    enabled,
  });
}
